import invariant from 'invariant';
import { call, put, select } from 'typed-redux-saga/macro';

import { GetActionByType } from 'Lib/reduxType';
import { withErrorHandling } from 'Lib/sagaHelpers';
import { getServiceFn } from 'Services';
import { ACCOUNT_SERVICE, AUTH_SERVICE } from 'Services/constants';

import {
  removeEmailError,
  removeEmailSuccess,
  sendEmailError,
  sendEmailSuccess,
  sendRecoveryEmailError,
  sendRecoveryEmailSuccess,
  verifyEmailError,
  verifyEmailSuccess,
} from 'Store/email/actions';
import {
  EMAIL_COLLECTION_SETTINGS_RECEIVED,
  REMOVE_EMAIL,
  SEND_EMAIL,
  SEND_RECOVERY_EMAIL,
  VERIFY_EMAIL,
  VERIFY_EMAIL_TYPE_GOOGLE,
  VERIFY_EMAIL_TYPE_OTP,
} from 'Store/email/constants';
import { TRIGGER_EMAIL_COLLECTION_PAYLOAD_NAME } from 'Store/email/tracking';
import { showNotification } from 'Store/notifications/actions';
import { fetchProfile } from 'Store/profile/actions';
import { getDeepLinkOtp } from 'Store/user/selectors';

import { trackEvent } from 'Store/sparks/actions';
import {
  AUTH_RECOVER_ACCOUNT,
  TRIGGER_RECEIVE,
  VERIFY_EMAIL_EVENT,
} from 'Store/sparks/sparks.events';

import {
  NOTIFICATION_THEME_ERROR,
  NOTIFICATION_THEME_SUCCESS,
} from 'Components/InAppNotification/constants';

const verifyEventAction = 'verifyEmail';
const verifyEventSource = 'client';
const verifyStatusFailed = 0; // backend verification failed
const verifyStatusSuccess = 1; // backend verification successful

export const sendEmailSaga = withErrorHandling(
  function* sendEmail(action: GetActionByType<typeof SEND_EMAIL>) {
    const { email } = action.payload || {};
    invariant(email, 'No email supplied');

    yield* call(yield* call(getServiceFn, ACCOUNT_SERVICE, 'sendEmail' as const), email);
    yield* put(sendEmailSuccess());
    // Show successful email sent notification
    yield* put(
      showNotification({
        theme: NOTIFICATION_THEME_SUCCESS,
        title: [{ id: 'EmailCollection.sendSuccessNotificationHeading' }],
        body: [{ id: 'EmailCollection.sendSuccessNotificationContent' }],
      })
    );
    yield* put(fetchProfile());
  },
  {
    name: 'sendEmailSaga',
    *errorHandler(err) {
      yield* put(sendEmailError(err));
    },
  }
);

export const cancelEmailVerifySaga = withErrorHandling(
  function* _cancelEmailVerifySaga() {
    yield* call(yield* call(getServiceFn, ACCOUNT_SERVICE, 'dismissEmail' as const));
    yield* put(sendEmailSuccess());
  },
  {
    name: 'cancelEmailVerifySaga',
  }
);

export const verifyEmailSaga = withErrorHandling(
  function* verifyEmail(action: GetActionByType<typeof VERIFY_EMAIL>) {
    const { googleIdToken, type } = action.payload;
    if (type === VERIFY_EMAIL_TYPE_OTP) {
      const otpToken = yield* select(getDeepLinkOtp);
      invariant(otpToken, 'No OTP token supplied');

      yield* call(yield* call(getServiceFn, ACCOUNT_SERVICE, 'verifyEmailOtp' as const), otpToken);
    } else if (type === VERIFY_EMAIL_TYPE_GOOGLE) {
      invariant(googleIdToken, 'No Google token supplied');

      yield* call(
        yield* call(getServiceFn, ACCOUNT_SERVICE, 'verifyEmailGoogle' as const),
        googleIdToken
      );
    }
    yield* put(verifyEmailSuccess());
    yield* put(
      trackEvent(VERIFY_EMAIL_EVENT, {
        eventAction: verifyEventAction,
        source: verifyEventSource,
        eventStatus: verifyStatusSuccess,
      })
    );

    // Update email in UI
    yield* put(fetchProfile());

    // Show success notification
    yield* put(
      showNotification({
        theme: NOTIFICATION_THEME_SUCCESS,
        title: [{ id: 'EmailCollection.verifySuccessNotificationHeading' }],
        body: [{ id: 'EmailCollection.verifySuccessNotificationContent' }],
      })
    );
  },
  {
    name: 'verifyEmailSaga',
    *errorHandler() {
      yield* put(verifyEmailError());
      yield* put(
        trackEvent(VERIFY_EMAIL_EVENT, {
          eventAction: verifyEventAction,
          source: verifyEventSource,
          eventStatus: verifyStatusFailed,
        })
      );
      // Show error notification
      yield* put(
        showNotification({
          theme: NOTIFICATION_THEME_ERROR,
          title: [{ id: 'somethingWentWrong' }],
          body: [{ id: 'EmailCollection.verifyErrorNotificationContent' }],
        })
      );
    },
  }
);

export const removeEmailSaga = withErrorHandling(
  function* removeEmail(action: GetActionByType<typeof REMOVE_EMAIL>) {
    const { token } = action.payload;
    invariant(token, 'No token supplied');

    yield* call(yield* call(getServiceFn, ACCOUNT_SERVICE, 'removeEmail' as const), token);
    yield* put(removeEmailSuccess());
  },
  {
    name: 'removeEmailSaga',
    *errorHandler() {
      yield* put(removeEmailError());
    },
  }
);

export const emailCollectionSettingsSaga = withErrorHandling(
  function* _emailCollectionSettingsSaga(
    action: GetActionByType<typeof EMAIL_COLLECTION_SETTINGS_RECEIVED>
  ) {
    const {
      emailPromptRequired,
      emailPromptDismissible,
      emailPromptShowMarketingOptIn,
      emailPromptShowStrictOptIn,
    } = action.payload;
    // Only tracking for now
    yield* put(
      trackEvent(TRIGGER_RECEIVE, {
        emailPromptDismissible,
        emailPromptRequired,
        emailPromptShowMarketingOptIn,
        emailPromptShowStrictOptIn,
        payloadName: TRIGGER_EMAIL_COLLECTION_PAYLOAD_NAME,
        type: 'websocket',
      })
    );
  },
  {
    name: 'emailCollectionSettingsSaga',
  }
);

// Account Recovery
export const sendRecoveryEmailSaga = withErrorHandling(
  function* (action: GetActionByType<typeof SEND_RECOVERY_EMAIL>) {
    const { email, from } = action.payload;
    yield* call(yield* call(getServiceFn, AUTH_SERVICE, 'requestEmailMagicLink' as const), email);
    yield* put(sendRecoveryEmailSuccess());
    yield* put(
      trackEvent(AUTH_RECOVER_ACCOUNT, {
        step: 'enterEmail',
        action: 'submit',
        value: null,
        source: from,
        status: 1,
      })
    );
  },
  {
    name: 'sendRecoveryEmailSaga',
    *errorHandler(e) {
      yield* put(sendRecoveryEmailError({ error: e }));
      yield* put(
        trackEvent(AUTH_RECOVER_ACCOUNT, {
          step: 'enterEmail',
          action: 'submit',
          value: null,
          status: 0,
        })
      );
    },
  }
);
